import React, { MutableRefObject, ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';

//import { Card } from 'components/card/card';
//import { useTranslate } from 'hooks/useTranslate';

import styles from './dropdown.module.css';
//import { Typography } from 'components/typography/typography';
import { useClickOutside } from 'hooks/use-click-outside';
//import { Input } from 'components/input/input';
import { Icon } from 'components/icon/icon';
import { Typography } from 'components/typography/typography';

export type OptionType<T> = {
	label: string;
	value?: T;
};

export type UnionOptionType<T extends any> = OptionType<T> | string;

type Props<T extends any> = {
	defaultValue?: string;
	onSelect: (option: UnionOptionType<T>) => void;
	options: UnionOptionType<T>[];
	renderItem?: (option: UnionOptionType<T>, selected: boolean) => ReactNode;
};

enum KEYS_CODES {
	UP = 'ArrowUp',
	DOWN = 'ArrowDown',
	ENTER = 'Enter',
	TAB = 'Tab',
}

/**
 * Dropdown Component
 */
export const Dropdown = <T extends any>(props: Props<T>) => {
	const getValue = (value: UnionOptionType<T>) => {
		if (typeof value === 'string') {
			return value;
		}

		return value.label;
	};

	const [value, setValue] = useState<string>(
		props.defaultValue || getValue(props.options[0])
	);
	const [isVisible, setOptionsVisible] = useState<boolean>(false);
	const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
	const listElement: MutableRefObject<null | HTMLInputElement> =
		useRef<null | HTMLInputElement>(null);

	useClickOutside(listElement, () => {
		setOptionsVisible(false);
	});

	const focusOption = (optionIndex: number) => {
		const element = document.querySelector<HTMLElement>(
			`[data-original-index='dropdown-${optionIndex}']`
		);

		if (element) {
			element.focus();

			setSelectedOptionIndex(optionIndex);
		}
	};

	const onKeyDownItemHandler = (
		event: React.KeyboardEvent<HTMLDivElement>
	) => {
		let nextSelectedOptionIndex = selectedOptionIndex;

		/*        if (event.key !== KEYS_CODES.TAB) {
            event.preventDefault();
        }*/

		switch (event.key) {
			case KEYS_CODES.DOWN: {
				nextSelectedOptionIndex = Math.min(
					props.options.length,
					selectedOptionIndex + 1
				);
				focusOption(nextSelectedOptionIndex);
				break;
			}
			case KEYS_CODES.UP: {
				nextSelectedOptionIndex = Math.max(0, selectedOptionIndex - 1);
				focusOption(nextSelectedOptionIndex);
				break;
			}
			case KEYS_CODES.ENTER: {
				onSelect(props.options[selectedOptionIndex - 1]);
				setValue(getValue(props.options[selectedOptionIndex - 1]));
				setOptionsVisible(false);
			}
		}
	};

	const onKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (!isVisible && event.key === KEYS_CODES.DOWN) {
			setOptionsVisible(true);
		}
		onKeyDownItemHandler(event);
	};

	const onClick = () => {
		setOptionsVisible(true);
		setSelectedOptionIndex(0);
	};

	const onSelect = (option: UnionOptionType<T>) => {
		props.onSelect(option);
		setValue(getValue(option));
		setOptionsVisible(false);
	};

	return (
		<div
			ref={listElement}
			role="button"
			onKeyDown={onKeyDownHandler}
			className={styles.container}
		>
			<div tabIndex={0} className={styles.select} onClick={onClick}>
				{props.renderItem ? props.renderItem(value, false) : value}
				<span className={styles.icon}>
					<Icon name="arrow" />
				</span>
			</div>
			{isVisible && props.options.length !== 0 && value && (
				<div className={styles.options}>
					{props.options.map(
						(option: UnionOptionType<T>, i: number) => (
							<Typography
								tabIndex={i}
								onClick={() => onSelect(option)}
								as="div"
								key={getValue(option)}
								color="main-white"
								fontSize="medium"
								className={classNames(styles.item, {
									[styles.hover]:
										i + 1 === selectedOptionIndex,
									[styles.selected]:
										getValue(option) === value,
								})}
								data-original-index={`dropdown-${i + 1}`}
							>
								{props.renderItem
									? props.renderItem(
											option,
											getValue(option) === value
									  )
									: getValue(option)}
							</Typography>
						)
					)}
				</div>
			)}
		</div>
	);
};
